import { API_TAG_TYPES, baseApi } from '@/store/baseApi';
import type {
  GetUserProfileArg,
  IGetUserProfileResponse,
} from '@/features/UserManagement/services/userApis';
import type { ApiResponse } from '../../../services/services.types';
import type {
  AuditLogItem,
  DepartmentItem,
  EmployeeListItem,
  GetAuditLogParams,
  GetRoleMatrixParams,
  ListEmployeesParams,
  MatrixResponse,
  PaginatedResponse,
  RevokeMatrixRolesRequest,
  RevokeMatrixRolesResponse,
  RolesApiResponse,
  SendInvitationRequest,
  UpdateMatrixRoleRequest,
  UpdateMatrixRoleResponse,
  ProvisionExternalAuditorRequest,
  ExternalAuditorItem,
  GetExternalAuditorsParams,
  UserManagementStatsResponse,
  UserManagementActorsResponse,
  DepartmentHeadAndDelegatesResponse,
  DepartmentDelegationListData,
  GetDepartmentDelegationParams,
  IGetAssignableModulesResponse,
  IEditExternalAuditorRequest,
} from './userManagement.types';

const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserProfile: builder.query<IGetUserProfileResponse, GetUserProfileArg>({
      query: (arg) => ({
        url: '/user/profile',
        params: arg?.roleCode ? { roleCode: arg.roleCode } : undefined,
      }),
      transformResponse: (response: ApiResponse<IGetUserProfileResponse>) => response.data,
      // Passing `roleCode` is how the role is switched: the server stores it as the user's
      // preferred role and scopes every role-dependent response (inboxes, registers, lists) by it.
      // Whatever was fetched or cached under the previous role is therefore stale, so once the
      // switch has gone through every tagged query is refetched.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        if (!arg?.roleCode) return;
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(baseApi.util.invalidateTags([...API_TAG_TYPES]));
      },
    }),
    getRoleMatrix: builder.query<MatrixResponse, GetRoleMatrixParams | void>({
      query: (params) => ({
        url: '/user-management/matrix',
        params: params
          ? {
              ...(params.page && { page: params.page }),
              ...(params.limit && { limit: params.limit }),
              ...(params.searchText && { searchText: params.searchText }),
              ...(params.departmentId && { departmentId: params.departmentId }),
              ...(params.roleName && { roleName: params.roleName }),
              ...(params.sortBy && { sortBy: params.sortBy }),
              ...(params.sortOrder && { sortOrder: params.sortOrder }),
            }
          : undefined,
      }),
      transformResponse: (response: ApiResponse<MatrixResponse>) => response.data,
      providesTags: ['RoleMatrix'],
    }),
    listEmployees: builder.query<PaginatedResponse<EmployeeListItem>, ListEmployeesParams>({
      query: (params) => ({
        url: '/user-management',
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
          sortOrder: params.sortOrder ?? 'DESC',
          sortBy: params.sortBy ?? 'firstName',
          ...(params.searchText && { searchText: params.searchText }),
          ...(params.department && { department: params.department }),
          ...(params.isActive !== undefined && { isActive: params.isActive }),
        },
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<EmployeeListItem>>) =>
        response.data,
      providesTags: ['Employees'],
    }),
    getDepartments: builder.query<DepartmentItem[], void>({
      query: () => ({ url: '/user-management/departments' }),
      transformResponse: (response: ApiResponse<DepartmentItem[]>) => response.data,
    }),
    updateMatrixRole: builder.mutation<UpdateMatrixRoleResponse, UpdateMatrixRoleRequest>({
      query: (body) => ({
        url: '/user-management/matrix/role',
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<UpdateMatrixRoleResponse>) => response.data,
      invalidatesTags: ['RoleMatrix', 'Delegate', 'Employees', 'AuditLog'],
    }),
    revokeMatrixRoles: builder.mutation<RevokeMatrixRolesResponse, RevokeMatrixRolesRequest>({
      query: (body) => ({
        url: '/user-management/revoke-role',
        method: 'POST',
        data: body,
      }),
      transformResponse: (response: ApiResponse<RevokeMatrixRolesResponse>) => response.data,
      invalidatesTags: ['RoleMatrix', 'Employees', 'AuditLog'],
    }),
    getAuditLog: builder.query<PaginatedResponse<AuditLogItem>, GetAuditLogParams>({
      query: (params) => ({
        url: '/user-management/audit-log',
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
          sortOrder: params.sortOrder ?? 'DESC',
          ...(params.searchText && { searchText: params.searchText }),
          ...(params.action && { action: params.action }),
          ...(params.actorUserId && { actorUserId: params.actorUserId }),
        },
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<AuditLogItem>>) => response.data,
      providesTags: ['AuditLog'],
    }),
    getRoles: builder.query<RolesApiResponse, void>({
      query: () => ({ url: '/roles' }),
      // Reuses RoleMatrix's tag so a Role Matrix save refetches this too (REG-BUG-008).
      providesTags: ['RoleMatrix'],
    }),
    sendInvitation: builder.mutation<void, SendInvitationRequest>({
      query: (body) => ({ url: '/user-management/invitations', method: 'POST', data: body }),
      invalidatesTags: ['AuditLog', 'RoleMatrix', 'Employees'],
    }),
    provisionExternalAuditor: builder.mutation<void, ProvisionExternalAuditorRequest>({
      query: (body) => ({ url: '/user-management/external-auditor', method: 'POST', data: body }),
      invalidatesTags: ['RoleMatrix', 'ExternalAuditors', 'AuditLog'],
    }),
    getExternalAuditors: builder.query<
      PaginatedResponse<ExternalAuditorItem>,
      GetExternalAuditorsParams
    >({
      query: (params) => ({
        url: '/user-management/external-auditors',
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          ...(params.searchText && { searchText: params.searchText }),
          ...(params.status && { status: params.status }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<ExternalAuditorItem>>) =>
        response.data,
      providesTags: ['ExternalAuditors'],
    }),
    revokeExternalAuditor: builder.mutation<void, string>({
      query: (auditorId) => ({
        url: `/user-management/external-auditors/${auditorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ExternalAuditors', 'AuditLog'],
    }),
    getUserManagementStats: builder.query<UserManagementStatsResponse, void>({
      query: () => ({ url: '/user-management/stats' }),
      transformResponse: (response: ApiResponse<UserManagementStatsResponse>) => response.data,
      providesTags: ['RoleMatrix', 'ExternalAuditors'],
    }),
    getAuditLogActors: builder.query<UserManagementActorsResponse[], void>({
      query: () => ({ url: '/user-management/audit-log/actors' }),
      transformResponse: (response: ApiResponse<UserManagementActorsResponse[]>) => response.data,
    }),
    getHeadAndDelegates: builder.query<
      DepartmentHeadAndDelegatesResponse[],
      GetDepartmentDelegationParams | void
    >({
      query: (params) => ({
        url: 'm4/departments/delegation',
        params: {
          ...(params?.departmentName && { searchText: params.departmentName }),
          ...(params?.sortBy && { sortBy: params.sortBy }),
          ...(params?.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      transformResponse: (response: ApiResponse<DepartmentDelegationListData>) =>
        response.data.items,
      providesTags: ['Delegate'],
    }),
    getIndividualDepartmentHeadAndDelegates: builder.query<
      DepartmentHeadAndDelegatesResponse,
      string
    >({
      query: (departmentId: string) => ({ url: `m4/departments/delegation/${departmentId}` }),
      transformResponse: (response: ApiResponse<DepartmentHeadAndDelegatesResponse>) =>
        response.data,
    }),
    postDelegates: builder.mutation<
      void,
      { departmentId: string; departmentHeadId: string; delegateUserIds: string[] }
    >({
      query: (body) => ({
        url: `m4/departments/delegation/${body.departmentId}`,
        method: 'PATCH',
        data: {
          departmentHeadId: body.departmentHeadId,
          delegateUserIds: body.delegateUserIds ?? [],
        },
      }),
      invalidatesTags: ['Delegate', 'AuditLog'],
    }),
    getAssignableModules: builder.query<IGetAssignableModulesResponse, void>({
      query: () => ({ url: '/user-management/modules' }),
      transformResponse: (response: ApiResponse<IGetAssignableModulesResponse>) => response.data,
    }),
    editExternalAuditor: builder.mutation<string, IEditExternalAuditorRequest>({
      query: ({ id, modules, accessEndDate, reason }) => ({
        url: `/user-management/external-auditors/${id}`,
        method: 'PATCH',
        data: { modules, accessEndDate, reason },
      }),
      transformResponse: (response: ApiResponse<string>) => response.data,
      invalidatesTags: ['ExternalAuditors', 'AuditLog'],
    }),
  }),
});

export const {
  useGetUserProfileQuery,
  useGetRoleMatrixQuery,
  useListEmployeesQuery,
  useLazyListEmployeesQuery,
  useGetDepartmentsQuery,
  useUpdateMatrixRoleMutation,
  useRevokeMatrixRolesMutation,
  useGetAuditLogQuery,
  useGetRolesQuery,
  useSendInvitationMutation,
  useProvisionExternalAuditorMutation,
  useGetExternalAuditorsQuery,
  useRevokeExternalAuditorMutation,
  useGetUserManagementStatsQuery,
  useGetAuditLogActorsQuery,
  useGetHeadAndDelegatesQuery,
  useGetIndividualDepartmentHeadAndDelegatesQuery,
  usePostDelegatesMutation,
  useGetAssignableModulesQuery,
  useEditExternalAuditorMutation,
} = usersApi;

export default usersApi;
